import React, { useCallback, useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import { shape, arrayOf, string, number } from 'prop-types';
import { useFeature } from 'use-feature';
import { PaymentClient } from '@azzurri-group/dna-payment-client';
import { markdownRemarkType } from 'common/propTypes';
import logger from 'common/utils/logger';
import { Layout } from 'common/components/Layout';
import SerialNumberModal from 'common/components/SerialNumberModal';
import { Splash } from 'common/components';
import { DNA_CLIENT_NAME } from 'common/constants/payment';
import { useConfig } from 'common/providers/configProvider';
import RestaurantsProvider from '../providers/restaurantsProvider';
import { Home } from '../components/Home';

const HomePage = ({
  data: {
    markdownRemark: { frontmatter: content },
    allRestaurant,
    errorList: {
      frontmatter: { errors },
    },
  },
  location: { search },
}) => {
  const { distance } = content;
  const data = allRestaurant.edges.map(({ node }) => node);

  const urlSearchParams = new URLSearchParams(search);

  const { restaurantId, tableNumber, checkNumber } = Object.fromEntries(urlSearchParams.entries());

  const { paymentMode } = useConfig();

  const [isLoading, setIsLoading] = useState(false);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    const pdqClient = new PaymentClient({
      clientName: DNA_CLIENT_NAME,
      debug: !!process.env.GATSBY_DNA_API_LOG_ENABLED,
    });

    const fetchDeviceInfo = async () => {
      try {
        const response = await pdqClient.getInfo();
        logger.info({ response }, 'Device Info Retrieved Successfully');
        window.localStorage.setItem('deviceSerialNumber', response.serialNumber);
        setIsLoading(false);
      } catch (err) {
        logger.error({ error: err }, 'Unable to retrieve serial number, prompt for it.');
        const deviceSerialNumber = window.localStorage.getItem('deviceSerialNumber');
        const isModalOpen = !!paymentMode && !deviceSerialNumber;
        setIsLoading(false);
        setIsOpen(isModalOpen);
      }
    };

    if (paymentMode && !window.localStorage.getItem('deviceSerialNumber')) {
      setIsLoading(true);
      fetchDeviceInfo();
    }
  }, [paymentMode]);

  const hideSerialNumberModal = useFeature({
    name: 'HIDE_SERIAL_NUMBER_MODAL',
    enabled: process.env.GATSBY_HIDE_SERIAL_NUMBER_MODAL === 'true',
  });

  const toggleModal = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  if (isLoading) {
    return <Splash />;
  }

  return (
    <RestaurantsProvider data={data} distance={distance}>
      <Layout currentPage="/" variant="filled" displayLogo>
        {isOpen && !hideSerialNumberModal ? (
          <SerialNumberModal toggleModal={toggleModal} />
        ) : (
          <Home
            errorList={errors}
            queryRestaurantId={restaurantId}
            queryTableNumber={tableNumber}
            queryCheckNumber={checkNumber}
            loading={!!restaurantId}
          />
        )}
      </Layout>
    </RestaurantsProvider>
  );
};

HomePage.propTypes = {
  data: shape({
    markdownRemark: markdownRemarkType.isRequired,
    allRestaurant: shape({
      edges: arrayOf(
        shape({
          node: shape({
            restaurantId: string.isRequired,
            address: shape({
              line1: string.isRequired,
              city: string.isRequired,
              postalCode: string,
            }).isRequired,
            name: string.isRequired,
            shortName: string.isRequired,
            coordinates: shape({
              latitude: number.isRequired,
              longitude: number.isRequired,
            }).isRequired,
          }).isRequired,
        })
      ).isRequired,
    }).isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }),
};

export default HomePage;

export const HomePageQuery = graphql`
  {
    errorList: markdownRemark(frontmatter: { path: { eq: "errors" } }) {
      frontmatter {
        errors {
          tableNotFound
          restaurantNotFound
          unableToJoinTable
        }
      }
    }
    markdownRemark(frontmatter: { path: { eq: "/" } }) {
      frontmatter {
        title
        newTitle
        subtitle
        useLocationBtn
        search {
          placeholder
          button
        }
        dividerText
        restaurants {
          placeholder
          newPlaceholder
        }
        tableNumber {
          label
          placeholder
          error
        }
        howToFind
        submitButton
        submitButtonPay
        qrReader {
          subtitle
          scanButton
          error
        }
        distance
        irelandRedirectMessage
        cookie {
          line1
          line2
          line3
          text
          link
        }
        privacy {
          text
          link
        }
        cookieConsentModal {
          title
          bodyText
          closeButtonText
        }
        link {
          href
          linkText
        }
        serialNumberModal {
          title
          subTitle
          infoAlert
          label
          errorAlert
          button
        }
      }
    }
    allRestaurant {
      edges {
        node {
          restaurantId
          address {
            line1
            city
            postalCode
          }
          name
          shortName
          coordinates {
            latitude
            longitude
          }
        }
      }
    }
  }
`;
